import { extractErrorInfo } from './errorInfo';

const USER_REJECTION_KEYWORDS = [
  'user rejected',
  'user denied',
  'user cancelled',
  'rejected the request',
  'transaction cancelled',
];

const WALLET_COMPATIBILITY_KEYWORDS = [
  'connected wallet cannot produce a valid ardrive upload signature',
  'wallet signature could not be verified locally',
  'invalid ardrive upload signature',
  'ledger-backed accounts',
  'cannot sign solana transactions using sign message',
];

export const ARDRIVE_WALLET_COMPATIBILITY_ERROR =
  'Connected wallet cannot produce a valid ArDrive upload signature. ' +
  'Turbo/ArDrive uploads require a wallet that can sign and return a ' +
  'valid Solana message signature; Ledger-backed accounts and some ' +
  'mobile wallets reject this flow. Use a different signer for this ' +
  'publisher wallet or choose another upload provider.';

function getErrorSearchText(error: unknown): string {
  return extractErrorInfo(error).searchText;
}

export function getErrorMessage(
  error: unknown,
  fallbackMessage = 'Unknown error',
): string {
  return extractErrorInfo(error, {
    defaultMessage: fallbackMessage,
  }).message;
}

export function isUserRejectedError(error: unknown): boolean {
  const lowerMsg = getErrorSearchText(error);
  return USER_REJECTION_KEYWORDS.some((keyword) => lowerMsg.includes(keyword));
}

export function isWalletCompatibilityError(error: unknown): boolean {
  const lowerMsg = getErrorSearchText(error);
  return (
    WALLET_COMPATIBILITY_KEYWORDS.some((keyword) =>
      lowerMsg.includes(keyword),
    ) ||
    (lowerMsg.includes('walletsignmessageerror') &&
      lowerMsg.includes('unexpected error'))
  );
}

export function normalizeMintingError(error: unknown): {
  message: string;
  isUserRejection: boolean;
} {
  const message = getErrorMessage(error);

  if (isUserRejectedError(error)) {
    return {
      message: 'Transaction cancelled by user',
      isUserRejection: true,
    };
  }

  if (isWalletCompatibilityError(error)) {
    return {
      message: ARDRIVE_WALLET_COMPATIBILITY_ERROR,
      isUserRejection: false,
    };
  }

  return {
    message,
    isUserRejection: false,
  };
}

export function formatMintingErrorMessage(error: unknown): string {
  const normalized = normalizeMintingError(error);
  if (normalized.isUserRejection) {
    return normalized.message;
  }

  const lowerMessage = normalized.message.toLowerCase();

  if (lowerMessage.includes('insufficient funds')) {
    return 'Insufficient SOL balance for transaction fees';
  }

  if (lowerMessage.includes('blockhash not found')) {
    return 'Transaction expired. Please try again.';
  }

  if (
    lowerMessage.includes('transport error') ||
    (lowerMessage.includes('sendmessage') && lowerMessage.includes('undefined'))
  ) {
    return 'Wallet communication failed. Reconnect or update your wallet, then try again.';
  }

  if (
    lowerMessage.includes('signature verification failure') ||
    lowerMessage.includes('signature verification failed')
  ) {
    return 'Your wallet did not return a valid transaction signature. Reconnect or update it, or use another wallet.';
  }

  if (
    lowerMessage.includes(
      'connected wallet does not support signing transactions',
    )
  ) {
    return 'Connected wallet cannot sign transactions. Reconnect or update it, or use another wallet.';
  }

  if (lowerMessage.includes('simulation failed')) {
    return 'Transaction simulation failed. Please check your wallet balance.';
  }

  return normalized.message || 'An unexpected error occurred';
}
